/**
 * Role-Based Access Control (RBAC) Configuration
 * Maps AWS Cognito user groups/roles to chat agents
 */

// Agent identifiers
export const AGENTS = {
  BROCHAT: 'brochat',
  CROWCHAT: 'crowchat',
};

// Cognito group/role names
// Backend returns: BroGroup, CrowGroup
export const COGNITO_ROLES = {
  BROCHAT_USER: 'BroGroup',
  CROWCHAT_USER: 'CrowGroup',
};

// Role-to-Agent mapping
export const ROLE_AGENT_MAP = {
  [COGNITO_ROLES.BROCHAT_USER]: AGENTS.BROCHAT,
  [COGNITO_ROLES.CROWCHAT_USER]: AGENTS.CROWCHAT,
};

// Default agent if no role matches
export const DEFAULT_AGENT = AGENTS.BROCHAT;

/**
 * Get the agent identifier based on user's Cognito roles
 * Supports both standard roles (BroGroup, CrowGroup) and org-specific roles (CrowGroup-{orgId})
 * @param {Array<string>} userRoles - Array of Cognito group names
 * @returns {string} - Agent identifier
 */
export const getAgentForRoles = (userRoles = []) => {
  if (!Array.isArray(userRoles) || userRoles.length === 0) {
    return DEFAULT_AGENT;
  }

  // Check for standard roles first
  const priorityRoles = [
    COGNITO_ROLES.BROCHAT_USER,
    COGNITO_ROLES.CROWCHAT_USER,
  ];

  for (const role of priorityRoles) {
    if (userRoles.includes(role)) {
      return ROLE_AGENT_MAP[role];
    }
  }

  // Check for org-specific roles: CrowGroup-{orgId}
  for (const role of userRoles) {
    if (role.startsWith('CrowGroup-')) {
      // Org user - return crowchat
      return AGENTS.CROWCHAT;
    }
  }

  return DEFAULT_AGENT;
};

/**
 * Check if user has access to a specific agent
 * @param {Array<string>} userRoles - Array of Cognito group names
 * @param {string} agentId - Agent identifier
 * @returns {boolean} - True if user has access
 */
export const hasAgentAccess = (userRoles = [], agentId) => {
  if (!Array.isArray(userRoles) || userRoles.length === 0) {
    return agentId === DEFAULT_AGENT;
  }

  return userRoles.some(role => ROLE_AGENT_MAP[role] === agentId);
};

/**
 * Get all available agents for a user
 * @param {Array<string>} userRoles - Array of Cognito group names
 * @returns {Array<string>} - Array of agent identifiers
 */
export const getAvailableAgents = (userRoles = []) => {
  if (!Array.isArray(userRoles) || userRoles.length === 0) {
    return [DEFAULT_AGENT];
  }

  const agents = new Set();
  userRoles.forEach(role => {
    if (ROLE_AGENT_MAP[role]) {
      agents.add(ROLE_AGENT_MAP[role]);
    }
  });

  return agents.size > 0 ? Array.from(agents) : [DEFAULT_AGENT];
};

/**
 * Validate if a role exists in configuration
 * @param {string} role - Cognito group name
 * @returns {boolean}
 */
export const isValidRole = (role) => {
  return Object.values(COGNITO_ROLES).includes(role);
};

/**
 * Validate if an agent exists
 * @param {string} agentId - Agent identifier
 * @returns {boolean}
 */
export const isValidAgent = (agentId) => {
  return Object.values(AGENTS).includes(agentId);
};
